"use client";

import dynamic from "next/dynamic";
import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useParams, usePathname } from "next/navigation";
import { currentPageInfo, trackEvent, withWhatsAppVisitTag } from "../lib/engageXClient";
import { bitrixSourceIds, stickyFooterCourseStorageKey } from "../lib/util";

// On-site messages from EngageX (pop-ups, slide-ins, bars, small notes).
// EngageX serves the script that decides and draws them; this component loads
// it once the page has finished loading and gives it a narrow way into the
// site, window.LP.onsite: facts about the page, the site's event tracking,
// WhatsApp, the site's own enquiry / demo / brochure forms, and the enquiry
// pipeline for a form inside a message (lib/onsiteLead.js). Messages never get
// the visitor's details or call the CRM themselves.
//
// Everything here is off until NEXT_PUBLIC_ENGAGEX_ONSITE_ENABLED is "true"
// (build time, like every NEXT_PUBLIC_ variable).

const EnquireModal = dynamic(() => import("./modal/EnquireModal"), { ssr: false });
const BookDemo = dynamic(() => import("./modal/BookDemo"), { ssr: false });

const ENABLED = process.env.NEXT_PUBLIC_ENGAGEX_ONSITE_ENABLED === "true";
const SRC =
  process.env.NEXT_PUBLIC_ENGAGEX_ONSITE_SRC || "https://engage.learnerspoint.org/onsite/v1/runtime.js";
// The "Website on-site message" source, once a Bitrix admin has created it.
// Until then these leads arrive as Enquire Now.
const SOURCE_ID = process.env.NEXT_PUBLIC_ENGAGEX_ONSITE_SOURCE_ID || bitrixSourceIds.enquireNow;
const WA = process.env.NEXT_PUBLIC_WHATS_APP_LINK || "";
// Payment and account pages never load the script.
const SKIP = /^\/(ar\/|en\/)?(pay-now|payment-response|bookings|auth|profile)(\/|$)/i;

// The course page the visitor is on, as StickyCourseTracker stores it
// ({ id, name, price, productId }); [] (a general enquiry) anywhere else.
// Deliberately not FooterStickyCta's productId = id: a Strapi id is not a
// Bitrix product, and Bitrix falls back to its default product without one.
const readCourse = () => {
  try {
    const course = JSON.parse(localStorage.getItem(stickyFooterCourseStorageKey) || "null");
    return course && typeof course === "object" ? course : [];
  } catch {
    return [];
  }
};

// Form tracking and the enquiry_submitted event name the message as
// onsite_<public message id>.
const sectionOf = (pid) =>
  `onsite_${String(pid || "").toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 16)}`;

// Bitrix note on the lead: which message, on which page (the FooterStickyCta
// "<title> | <url>" field).
const leadNote = (headline) => {
  const title = String(headline ?? "").replace(/\s+/g, " ").trim().slice(0, 60);
  return {
    UF_CRM_1775825205171: `On-site message${title ? `: ${title}` : ""} | ${window.location.origin}${window.location.pathname}`,
  };
};

const openWhatsApp = (text) => {
  if (!WA) return false;
  let href;
  try {
    const url = new URL(WA);
    url.searchParams.delete("text");
    const rest = url.searchParams.toString();
    const message = String(text ?? "").trim().slice(0, 500) || "Hello";
    href = `${url.origin}${url.pathname}?${rest ? `${rest}&` : ""}text=${encodeURIComponent(message)}`;
  } catch {
    return false;
  }
  trackEvent("whatsapp_clicked", { location: "onsite" });
  window.open(withWhatsAppVisitTag(href), "_blank", "noopener");
  return true;
};

// The enquiry pipeline is loaded when a message's form is opened (see track
// below), or on the first submit at the latest; a failed load is tried again.
let leadModule = null;
const loadLead = () =>
  (leadModule ||= import("../lib/onsiteLead").catch((error) => {
    leadModule = null;
    throw error;
  }));

// One enquiry at a time: a second press while one is being sent gets the same
// answer.
let pendingLead = null;
const submitLead = (payload) => {
  if (pendingLead) return pendingLead;
  const course = readCourse();
  const bitrixFields = leadNote(payload?.headline);
  pendingLead = loadLead()
    .then(({ submitOnsiteLead }) => submitOnsiteLead(payload, { course, sourceId: SOURCE_ID, bitrixFields }))
    .catch(() => ({ ok: false, error: "failed" }))
    .finally(() => {
      pendingLead = null;
    });
  return pendingLead;
};

// A Studio "Preview on the website" link carries #onsite_preview=<token>
// (older links ?onsite_preview=). It comes out of the address bar as the page
// starts, before analytics record the address or someone copies it, and the
// script gets it as LP.onsite.previewToken. The rest of the address is kept.
let previewToken = "";
const takePreviewToken = () => {
  const { pathname, search, hash } = window.location;
  const inHash = /(^#|&)onsite_preview=([^&]*)/.exec(hash);
  const params = new URLSearchParams(search);
  const inQuery = params.get("onsite_preview");
  if (!inHash && inQuery === null) return;
  try {
    previewToken = (inHash ? decodeURIComponent(inHash[2]) : inQuery).slice(0, 600);
  } catch {
    previewToken = "";
  }
  params.delete("onsite_preview");
  const rest = params.toString();
  let restHash = inHash ? hash.replace(inHash[0], inHash[1] === "#" ? "#" : "").replace(/^#&/, "#") : hash;
  if (restHash === "#") restHash = "";
  window.history.replaceState(window.history.state, "", `${pathname}${rest ? `?${rest}` : ""}${restHash}`);
  // Next.js's router starts listening to history calls in its own effect,
  // which runs after this one: tell it the new address too, so it never
  // writes the old one back.
  window.setTimeout(() => {
    window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}${window.location.hash}`);
  }, 0);
};

// The script loads once per tab, after `load` and then an idle moment (the
// FullLayout footer pattern; GTM and Rybbit load as late). A first page that is
// skipped loads it on the next page that is not.
let runtimeScheduled = false;
const scheduleRuntime = () => {
  if (runtimeScheduled || SKIP.test(window.location.pathname)) return;
  runtimeScheduled = true;
  const inject = () => {
    if (document.getElementById("lp-onsite-runtime")) return;
    const script = document.createElement("script");
    script.id = "lp-onsite-runtime";
    script.src = SRC;
    script.async = true;
    document.body.appendChild(script);
  };
  const idle = () => {
    if ("requestIdleCallback" in window) window.requestIdleCallback(inject, { timeout: 4000 });
    else window.setTimeout(inject, 1500);
  };
  if (document.readyState === "complete") idle();
  else window.addEventListener("load", idle, { once: true });
};

let formCount = 0;

export default function EngageXOnsite() {
  const pathname = usePathname();
  const params = useParams();
  const slug = useRef(null);
  slug.current = typeof params?.slug === "string" ? params.slug : null;
  const [form, setForm] = useState(null);

  // The bridge is in place before the script can load, so it never waits.
  useEffect(() => {
    if (!ENABLED) return undefined;
    takePreviewToken();

    const page = () => {
      const info = currentPageInfo();
      const stored = readCourse();
      const brochure = window.LP?.brochure;
      return {
        page_type: info.page_type,
        language: info.language,
        course_name: info.course_name || (typeof stored?.name === "string" ? stored.name : "") || "",
        category: info.category || "",
        path: window.location.pathname,
        slug: slug.current,
        hasBrochure: Boolean(brochure && brochure.path === window.location.pathname && brochure.available),
      };
    };

    // BookDemo looks the course up by the page's slug, so it only works on a
    // course page; anywhere else (blog posts and categories have slugs too)
    // the enquiry form opens instead.
    const onCoursePage = () => {
      const type = currentPageInfo().page_type;
      return Boolean(slug.current) && (type === "course" || type === "location");
    };

    const open = (kind, opts) => {
      const d = opts && typeof opts === "object" ? opts : {};
      const section = sectionOf(d.pid);
      if (kind === "brochure") {
        // BrochureCourse (course hero) answers when this course has a brochure.
        const ev = new CustomEvent("lp:open-brochure", { cancelable: true, detail: { section } });
        window.dispatchEvent(ev);
        return ev.defaultPrevented;
      }
      if (kind !== "enquiry" && kind !== "demo") return false;
      const next = {
        key: ++formCount,
        kind: kind === "demo" && onCoursePage() ? "demo" : "enquiry",
        course: readCourse(),
        section,
        corporate: d.corporate === true,
        bitrixFields: leadNote(d.headline),
      };
      try {
        flushSync(() => setForm(next));
      } catch {
        setForm(next);
      }
      return true;
    };

    const bridge = {
      v: 1,
      previewToken,
      page,
      track: (name, props) => {
        // Someone is looking at a message's form: load the enquiry pipeline now,
        // so the first press of Send does not wait for it.
        if (name === "form_open" && props?.form_name === "onsite") loadLead().catch(() => {});
        trackEvent(name, props);
      },
      whatsapp: openWhatsApp,
      open,
      submitLead,
    };
    window.LP = window.LP || {};
    window.LP.onsite = bridge;
    return () => {
      if (window.LP?.onsite === bridge) delete window.LP.onsite;
    };
  }, []);

  // A new page for the script's rules after every client-side navigation, and
  // the script itself once per tab.
  useEffect(() => {
    if (!ENABLED || !pathname) return undefined;
    const timer = window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent("lp:pageview", { detail: { path: window.location.pathname } }));
    }, 0);
    scheduleRuntime();
    return () => window.clearTimeout(timer);
  }, [pathname]);

  if (!ENABLED || !form) return null;

  // Closing unmounts the form. EnquireModal reports a close only when its
  // thank-you screen is closed, so the thank-you still shows.
  const close = (isOpen) => {
    if (isOpen !== true) setForm(null);
  };

  // "Use the corporate enquiry form" opens it with Corporate already chosen; the
  // lead type then follows what the visitor finally picks.
  return form.kind === "demo" ? (
    <BookDemo key={form.key} isOpen onOpenChange={close} section={form.section} />
  ) : (
    // No `title`: EnquireModal would use it as the Zoho source name and the heading.
    <EnquireModal
      key={form.key}
      isOpen
      onOpenChange={close}
      course={form.course}
      section={form.section}
      formName="enquire"
      defaultCorporate={form.corporate}
      bitrixSourceId={SOURCE_ID}
      bitrixFields={form.bitrixFields}
    />
  );
}
